import { hmsToSeconds, isValidHMS, secondsToHMS } from '@lib/utils/time';
import { useCallback, useRef } from 'react';
import { ClipStore } from '../types';

interface UseVideoControlsParams {
  video1Ref: React.RefObject<HTMLVideoElement | null>;
  video2Ref: React.RefObject<HTMLVideoElement | null>;
  activeVideo: 'video1' | 'video2';
  scrubTo: (time: number) => void;
  store: ClipStore;
}

export const useVideoControls = ({
  video1Ref,
  video2Ref,
  activeVideo,
  scrubTo,
  store,
}: UseVideoControlsParams) => {
  const timeInputRef = useRef<HTMLInputElement>(null);

  const togglePlayPause = useCallback(() => {
    if (store.isPlaying) {
      const activeVideoRef = activeVideo === 'video1' ? video1Ref : video2Ref;
      const video = activeVideoRef.current;
      if (video) {
        video.pause();
      }
      store.setIsPlaying(false);
    } else {
      const isAtEnd = store.currentTime >= store.totalClipsDuration - 0.1;

      if (isAtEnd) {
        store.setIsPlaying(true);
        scrubTo(0);
      } else {
        const activeVideoRef = activeVideo === 'video1' ? video1Ref : video2Ref;
        const video = activeVideoRef.current;
        if (video) {
          store.setIsPlaying(true);
          video.play().catch((error) => {
            console.error('Video playback failed:', error);
            store.setIsPlaying(false);
          });
        }
      }
    }
  }, [store.isPlaying, store.currentTime, store.totalClipsDuration, activeVideo, scrubTo]);

  const toggleFullscreen = useCallback(() => {
    if (!store.isFullscreen) {
      // Request fullscreen on the video container
      const videoContainer = video1Ref.current?.closest('[data-video-container]');
      if (videoContainer?.requestFullscreen) {
        videoContainer.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }, [store.isFullscreen]);

  const toggleMute = useCallback(() => {
    if (store.isMuted) {
      store.setVolume(store.prevVolume);
      store.setIsMuted(false);
    } else {
      store.setPrevVolume(store.volume);
      store.setVolume(0);
      store.setIsMuted(true);
    }
  }, [store.isMuted, store.prevVolume, store.volume]);

  const handleVolumeChange = useCallback(
    (_event: Event, newValue: number | number[]) => {
      const value = newValue as number;
      store.setVolume(value);
      store.setIsMuted(value === 0);
    },
    [store],
  );

  const handleTimeInputSubmit = useCallback(
    (timeInputValue: string | null) => {
      if (!timeInputValue || !isValidHMS(timeInputValue.trim())) return;

      const targetTime = hmsToSeconds(timeInputValue.trim());
      const realDuration = store.totalClipsDuration;
      const adjustedTime = Math.max(0, Math.min(targetTime, realDuration));

      scrubTo(adjustedTime);
      return '';
    },
    [store.totalClipsDuration, scrubTo],
  );

  const formatTime = useCallback((seconds: number): string => {
    return secondsToHMS(Math.max(0, seconds));
  }, []);

  const formatRemainingTime = useCallback(
    (seconds: number): string => {
      const realDuration = store.totalClipsDuration;
      const remainingTime = Math.max(0, Math.floor(realDuration - seconds));
      return `-${secondsToHMS(remainingTime)}`;
    },
    [store.totalClipsDuration],
  );

  const getVolumeIcon = useCallback(() => {
    if (store.volume === 0 || store.isMuted) {
      return 'muted';
    } else if (store.volume < 50) {
      return 'low';
    } else {
      return 'high';
    }
  }, [store.volume, store.isMuted]);

  return {
    timeInputRef,
    togglePlayPause,
    toggleFullscreen,
    toggleMute,
    handleVolumeChange,
    handleTimeInputSubmit,
    formatTime,
    formatRemainingTime,
    getVolumeIcon,
  };
};
